import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useLocale } from "../i18n";
import { fetchRegion, fetchListings } from "../lib/api";
import { fetchListings as fetchListingsExplicit } from "../lib/api"; // eslint-disable-line
import SEO, { buildBreadcrumb } from "../components/SEO";
import { ArrowLeft, ArrowUpRight, Filter } from "lucide-react";

const TYPE_LABELS = {
  accommodation: { en: "Accommodation", ro: "Cazare" },
  hotel:         { en: "Hotel",         ro: "Hotel" },
  guesthouse:    { en: "Guesthouse",    ro: "Pensiune" },
  cabin:         { en: "Cabin",         ro: "Cabană" },
  restaurant:    { en: "Restaurant",    ro: "Restaurant" },
  attraction:    { en: "Attraction",    ro: "Atracție" },
  museum:        { en: "Museum",        ro: "Muzeu" },
  castle:        { en: "Castle",        ro: "Castel" },
  nature:        { en: "Nature",        ro: "Natură" },
  activity:      { en: "Activity",      ro: "Activitate" },
};

const SORTS = [
  { value: "featured", en: "Featured", ro: "Recomandate" },
  { value: "name",     en: "Name A→Z", ro: "Nume A→Z" },
  { value: "price_asc",en: "Price ↑",  ro: "Preț ↑" },
  { value: "recent",   en: "Recently added", ro: "Adăugate recent" },
];

export default function RegionDetailFull() {
  const { slug } = useParams();
  const { locale, t } = useLocale();
  const [region, setRegion] = useState(null);
  const [listings, setListings] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [sort, setSort] = useState("featured");

  useEffect(() => { fetchRegion(slug).then(setRegion).catch(() => {}); }, [slug]);
  useEffect(() => {
    const params = { region_slug: slug, sort };
    if (filterType) params.listing_type = filterType;
    if (filterCity) params.city = filterCity;
    fetchListings(params).then(d => setListings(d.listings || [])).catch(() => setListings([]));
  }, [slug, filterType, filterCity, sort]);

  const cities = useMemo(() => Array.from(new Set(listings.map(l => l.city).filter(Boolean))).sort(), [listings]);
  const typesInUse = useMemo(() => Array.from(new Set(listings.map(l => l.listing_type))).sort(), [listings]);

  if (!region) return <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 text-[var(--wr-ink-2)]">{t("common.loading")}</div>;

  const nameKey = locale === "ro" ? "name_ro" : "name_en";
  const taglineKey = locale === "ro" ? "tagline_ro" : "tagline_en";
  const descKey = locale === "ro" ? "description_ro" : "description_en";

  const touristLd = {
    "@context": "https://schema.org", "@type": "TouristDestination",
    name: region[nameKey], description: region[descKey],
    url: `https://wowromania.com/regions/${slug}`, image: region.image,
    containedInPlace: { "@type": "Country", name: "Romania" },
    inLanguage: locale,
  };

  return (
    <article data-testid={`region-detail-${slug}`}>
      <SEO title={region[nameKey]} description={region[taglineKey]} path={`/regions/${slug}`} image={region.image} type="article"
        jsonLd={[touristLd, buildBreadcrumb([
          { name: locale === "ro" ? "Acasă" : "Home", path: "/" },
          { name: locale === "ro" ? "Destinații" : "Destinations", path: "/regions" },
          { name: region[nameKey], path: `/regions/${slug}` },
        ])]} />
      <div className="relative h-[60vh] overflow-hidden">
        <img src={region.image} alt={region[nameKey]} className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: region.image_position }} fetchPriority="high" decoding="async" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 h-full flex flex-col justify-end pb-16 text-white">
          <Link to="/regions" className="inline-flex items-center gap-2 font-mono-wr text-[11px] uppercase tracking-widest mb-6 hover:text-[var(--wr-red)] transition-colors" data-testid="back-to-regions">
            <ArrowLeft size={14} /> {locale === "ro" ? "Toate destinațiile" : "All destinations"}
          </Link>
          <h1 className="font-editorial text-6xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tight">{region[nameKey]}</h1>
          <p className="mt-4 max-w-2xl text-[18px] text-white/90">{region[taglineKey]}</p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 lg:py-16 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <p className="font-editorial text-2xl leading-relaxed text-[var(--wr-ink)]">{region[descKey]}</p>
        </div>
        <aside className="lg:col-span-4 border-t lg:border-t-0 lg:border-l border-[var(--wr-border)] pt-6 lg:pt-0 lg:pl-8">
          <p className="font-mono-wr text-[10px] uppercase tracking-[0.2em] text-[var(--wr-ink-2)] mb-4">{t("regions.highlights")}</p>
          <ul className="space-y-3">
            {region.highlights.map((h, i) => (
              <li key={i} className="flex items-baseline gap-3 text-[15px] text-[var(--wr-ink)] border-b border-[var(--wr-border)] pb-2">
                <span className="font-mono-wr text-[10px] text-[var(--wr-red)]">0{i + 1}</span>{h}
              </li>
            ))}
          </ul>
        </aside>
      </div>

      {/* LISTINGS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24" data-testid="region-listings">
        <div className="flex items-end justify-between mb-8 gap-6 flex-wrap">
          <div>
            <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)] mb-2">/ {locale === "ro" ? "LOCURI" : "PLACES"}</p>
            <h2 className="font-editorial text-4xl sm:text-5xl leading-tight tracking-tight text-[var(--wr-ink)]">
              {locale === "ro" ? `${listings.length} de descoperit` : `${listings.length} to discover`}
            </h2>
          </div>
          <div className="flex items-center gap-3 flex-wrap font-mono-wr text-[11px]" data-testid="listing-filters">
            <select value={filterType} onChange={(e)=>setFilterType(e.target.value)} className="border border-[var(--wr-border)] bg-[var(--wr-surface)] px-3 py-2" data-testid="filter-type">
              <option value="">{locale === "ro" ? "Toate tipurile" : "All types"}</option>
              {typesInUse.map(tt => <option key={tt} value={tt}>{TYPE_LABELS[tt]?.[locale] || tt}</option>)}
            </select>
            <select value={filterCity} onChange={(e)=>setFilterCity(e.target.value)} className="border border-[var(--wr-border)] bg-[var(--wr-surface)] px-3 py-2" data-testid="filter-city">
              <option value="">{locale === "ro" ? "Toate orașele" : "All cities"}</option>
              {cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={sort} onChange={(e)=>setSort(e.target.value)} className="border border-[var(--wr-border)] bg-[var(--wr-surface)] px-3 py-2" data-testid="filter-sort">
              {SORTS.map(s => <option key={s.value} value={s.value}>{s[locale]}</option>)}
            </select>
          </div>
        </div>

        {listings.length === 0 ? (
          <div className="border border-[var(--wr-border)] p-16 text-center" data-testid="empty-state">
            <p className="font-editorial text-4xl text-[var(--wr-ink)] mb-3">
              {locale === "ro" ? "În curând." : "Coming soon."}
            </p>
            <p className="text-[15px] text-[var(--wr-ink-2)] max-w-md mx-auto leading-relaxed">
              {locale === "ro"
                ? "Adăugăm în curând locuri verificate pentru această regiune. Revino peste puțin timp sau întreabă Ghidul."
                : "We're adding verified places for this region soon. Check back shortly or ask the Concierge."}
            </p>
            <Link to="/concierge" className="mt-6 inline-flex items-center gap-2 px-5 py-3 bg-[var(--wr-ink)] text-[var(--wr-bg)] hover:bg-[var(--wr-red)] transition-colors font-mono-wr text-[11px] uppercase tracking-[0.2em]">
              {locale === "ro" ? "Întreabă Ghidul" : "Ask the Concierge"} <ArrowUpRight size={14} />
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {listings.map(l => <ListingCard key={l.id} l={l} locale={locale} />)}
          </div>
        )}
      </section>
    </article>
  );
}

function ListingCard({ l, locale }) {
  const name = l[`name_${locale}`] || l.name_en;
  const desc = l[`short_description_${locale}`] || l.short_description_en;
  const price = l[`price_text_${locale}`] || l.price_text_en;
  const highlights = (l[`highlights_${locale}`] && l[`highlights_${locale}`].length ? l[`highlights_${locale}`] : l.highlights_en) || [];
  return (
    <article className="wr-sharp-card grid grid-cols-1 md:grid-cols-[320px_1fr_auto] gap-0 overflow-hidden hover:border-[var(--wr-ink)]" data-testid={`listing-card-${l.slug}`}>
      <div className="wr-img-mask h-56 md:h-full">
        {l.hero_image_url ? (
          <img src={l.hero_image_url} alt={name} className="w-full h-full object-cover" loading="lazy" decoding="async" />
        ) : <div className="w-full h-full bg-[var(--wr-surface-2)]" />}
      </div>
      <div className="p-6 md:p-8 flex flex-col gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-red)] border border-[var(--wr-red)] px-2 py-0.5">
            {TYPE_LABELS[l.listing_type]?.[locale] || l.listing_type}
          </span>
          {l.featured && (
            <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink)] border border-[var(--wr-ink)] px-2 py-0.5" data-testid="featured-badge">
              {locale === "ro" ? "Recomandat" : "Featured"}
            </span>
          )}
          {l.sponsored && (
            <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] border border-[var(--wr-border)] px-2 py-0.5">
              {locale === "ro" ? "Sponsorizat" : "Sponsored"}
            </span>
          )}
          {(l.city || l.locality) && (
            <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">
              {[l.locality, l.city].filter(Boolean).join(" · ")}
            </span>
          )}
        </div>
        <h3 className="font-editorial text-3xl leading-tight tracking-tight text-[var(--wr-ink)]">{name}</h3>
        {desc && <p className="text-[15px] leading-relaxed text-[var(--wr-ink-2)]">{desc}</p>}
        {highlights.length > 0 && (
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-[var(--wr-ink-2)]">
            {highlights.slice(0, 4).map((h, i) => <li key={i}>· {h}</li>)}
          </ul>
        )}
      </div>
      <div className="p-6 md:p-8 border-t md:border-t-0 md:border-l border-[var(--wr-border)] flex flex-col justify-between md:min-w-[200px]">
        {price && (
          <div className="mb-4">
            <p className="font-mono-wr text-[9px] uppercase tracking-widest text-[var(--wr-ink-2)] mb-1">{locale === "ro" ? "Preț" : "Price"}</p>
            <p className="font-editorial text-xl text-[var(--wr-ink)] leading-tight">{price}</p>
          </div>
        )}
        <Link to={`/listings/${l.slug}`} className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-[var(--wr-ink)] text-[var(--wr-bg)] hover:bg-[var(--wr-red)] transition-colors font-mono-wr text-[11px] uppercase tracking-[0.2em]" data-testid={`view-details-${l.slug}`}>
          {locale === "ro" ? "Vezi detalii" : "View details"} <ArrowUpRight size={14} />
        </Link>
      </div>
    </article>
  );
}
